<template>
  <div class="unit-group" :id="getAnchorIdString(groupTitle)">
    <div class="unit-group__alert page-alert danger" v-if="tooManyUnits">
      <div class="page-alert__icon"><i class="far fa-exclamation-circle"></i></div>
      <div>
        <div class="page-alert__title">You've selected too many units for {{ groupTitle }}</div>
        <div class="page-alert__body">Please remove units from this section</div>
      </div>
    </div>
    <div class="unit-group__head d-flex flex-row align-items-center justify-content-between">
      <div class="flex-shrink-0">
        <h3 class="unit-group__head-title">{{ groupTitle }}</h3>
        <span class="unit-group__head-helptip" v-if="getTooltip()" :id="`tooltip-${ getTooltip() }`"><i class="fal fa-info-circle"></i></span>
      </div>
      <div class="page-alert warning mt-n2 ml-4 mb-1 flex-grow-1" v-if="reviewMode && missingUnitsFromSavedPlan.length > 0">
        <i class="page-alert__icon far fa-exclamation-circle"></i>
        <div class="page-alert__body">
          <p>{{missingUnitsFromSavedPlan.length === 1 ? ' 1 selection is' : `${missingUnitsFromSavedPlan.length} selections are`}} no longer available.</p>
        </div>
      </div>
    </div>

    <div class="unit-group__description" v-if="container.description.length">{{ container.description }}
    </div>

    <div class="unit-group__body">
      <div class="unit-group__body-progress">
        <div class="progress-bar">
          <div class="progress-bar__current-value" :style="{width: progressBarWidth, background: progressBarColor}">
          </div>
        </div>
        <div class="unit-group__body-progress-count">{{ credits }} / {{ maxCredits + ( isOptionContainer ? extraRequired : 0) }}</div>
        <div class="unit-group__body-progress-label">points selected</div>
      </div>
      <div class="unit-group__body-units">
        <template v-if="containers && containers.length">
          <UnitGroupCategory v-for="(con,index) in containers" :key="index"
            :container="con" :location="getContainerLocation(con)" :firstLevelContainer="container" :canAdd="true"
            :autoSelectMandatory="con.connector==='AND' && container.connector==='AND'" :showContainerOption="true" />
        </template>
        <!--
          if this unit group doesn't have containers but has relationships, or condition sets
          make itself the group category
        -->
        <template v-else>
          <template v-if="relationships && relationships.length || this.container.conditionSet && this.container.conditionSet.length">
            <UnitGroupCategory :container="container" :location="location" :firstLevelContainer="container" :canAdd="true"
              :autoSelectMandatory="container.connector==='AND'" />
          </template>
        </template>
      </div>
    </div>

    <b-tooltip v-if="getTooltip()" :target="`tooltip-${ getTooltip() }`" triggers="hover focus" placement="right">
      {{ __(`label:${ getTooltip() } tooltip`) }}
    </b-tooltip>

  </div>
</template>

<script>
import { dynamicSort } from 'common/helpers/dynamic-sort.js'
import creditChangeCalculator from 'client/js/utils/credit-change-calculator.js'
import SelectionLocationHelper from 'client/js/utils/selection-location-helper.js'
import { onlyUnique } from 'common/helpers/only-unique'

import { mapGetters, mapActions, mapMutations } from 'vuex'
import UnitGroupCategory from './UnitGroupCategory.vue'
import SlideOutTreeView from './slide-out-tree-view/SlideOutTreeView.vue'

export default {
  props: {
    groupTitle: {
      type: String,
      required: true
    },
    container: {
      type: Object,
      required: true
    },
    location: {
      type: String,
      required: true
    },
    mandatory: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      slideOutComponent: SlideOutTreeView,
      slideOutComponentProperties: {
        container: this.container,
        location: this.location,
        mandatory: this.mandatory,
        firstLevelContainer: this.container
      },
      fillDefault: '#2372A7',
      fillWarning: '#99600B',
      fillDanger: '#62090E',
      fillSuccess: '#317817'
    }
  },
  methods: {
    // __:i18n,
    ...mapMutations('global', {
      openSlideOut: 'openSlideOut'
    }),
    ...mapActions({
      addItem: 'units/addCurriculumItem',
      removeItem: 'units/removeCurriculumItem'
    }),
    add: function (relationship, location) {
      this.addItem({ relationship, location })
    },
    remove: function (payload) {
      this.removeItem({
        location: payload.location,
        relationship: payload.relationship,
        recursive: true
      })
    },
    getContainerLocation(container) {
      return SelectionLocationHelper.getContainerLocation(this.location, container)
    },
    getTooltip() {
      if (this.groupTitle.toLowerCase().trim().includes('spine')) {
        return 'spine'
      }
      if (this.groupTitle.toLowerCase().trim().includes('core')) {
        return 'core'
      }
      if (this.groupTitle.toLowerCase().trim().includes('major')) {
        return 'major'
      }
      if (this.groupTitle.toLowerCase().trim().includes('option')) {
        return 'option'
      }
      return false
    },
    getAnchorIdString(string) {
      return 'anchor-' + string.replace(/\s/g, '').toLowerCase().trim()
    }
  },
  computed: {
    ...mapGetters({
      savedPlan: 'savedCourse/getSavedCoursePlan',

      selectedItemsByLocation: 'units/getSelectedCurriculumItemByLocation',
      totalCredits: 'units/getTotalCredits',

      savedMissingUnits: 'savedCourse/getMissingUnits'
    }),
    missingUnitsFromSavedPlan: function () {
      return this.savedMissingUnits?.filter((u) => u.indexOf(this.location) > -1).filter(onlyUnique) || []
    },
    reviewMode: function () {
      return this.savedPlan != null
    },
    credits() {
      return this.totalCredits(this.location, true)
    },
    maxCredits() {
      return parseInt(this.container.credits) || 0
    },
    progressBarWidth() {
      // Turns selected unit points to a % for progress bar
      if (this.isOptionContainer) {
        return (100 / (this.maxCredits + this.extraRequired)) * this.credits + '%'
      }
      return (100 / this.maxCredits) * this.credits + '%'
    },
    progressBarColor() {
      if (this.allUnitsSelected) {
        return this.fillSuccess
      } else if (this.tooManyUnits) {
        return this.fillDanger
      }
      return this.fillDefault
    },
    containers: function () {
      return this.container.container.slice().sort(dynamicSort('order'))
    },
    relationships: function () {
      return this.container.relationship.slice().sort(dynamicSort('order'))
    },
    isOptionContainer() {
      if (this.groupTitle == 'Option' || this.groupTitle == 'Options') {
        return true
      }
      return false
    },
    extraRequired() {
      return this.totalCredits('root', true, true) - this.totalCredits('root', false, true)
    },
    allUnitsSelected() {
      if (!this.isOptionContainer && this.credits == this.maxCredits) {
        return true
      } else if (this.isOptionContainer && this.credits == this.maxCredits + this.extraRequired) {
        return true
      }
      return false
    },
    tooManyUnits() {
      if (!this.isOptionContainer && this.credits > this.maxCredits) {
        return true
      } else if (this.isOptionContainer && this.credits > this.maxCredits + this.extraRequired) {
        return true
      }
      return false
    }
  },
  mounted() {},
  beforeDestroy() {},
  components: { UnitGroupCategory, SlideOutTreeView }
}
</script>
